import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { ThemedCheckbox, ThemedSubmitButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

interface NearbyPharmacyForm {
  readonly location: string;
  readonly useCurrentLocation: boolean;
}

export const CURRENT_LOCATION = "currentlocation";

export const NearbyPharmacySearchForm: FC = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchNear = searchParams.get("searchNear");
  const latitude = searchParams.get("latitude");
  const longitude = searchParams.get("longitude");
  const alert = useAlert();
  const [currentLocation, setCurrentLocation] = useState({ latitude: undefined, longitude: undefined });

  const defaultValues: NearbyPharmacyForm = useMemo(() => {
    return {
      location: !!searchNear && !latitude && !longitude ? searchNear : "",
      useCurrentLocation: !!latitude && !!longitude,
    };
  }, [searchNear]);

  const searchNearbyPharmacyForm = useForm<NearbyPharmacyForm>({
    defaultValues: defaultValues,
  });
  const navigate = useNavigateClient();
  const useCurrentLocation = searchNearbyPharmacyForm.watch("useCurrentLocation");

  const onSearchSubmitted = useCallback(
    async (formData: NearbyPharmacyForm) => {
      let queryString;
      if (formData.useCurrentLocation) {
        const lat = encodeURIComponent(currentLocation.latitude);
        const lng = encodeURIComponent(currentLocation.longitude);
        queryString = `latitude=${lat}&longitude=${lng}`;
      } else {
        queryString = `searchNear=${formData.location}`;
      }
      navigate(`/nearby-pharmacy/compare-pharmacies?${queryString}`);
    },
    [currentLocation],
  );

  useEffect(() => {
    if (useCurrentLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          searchNearbyPharmacyForm.setValue("location", "");
        },
        () => {
          alert({
            message: translate("NEARBY_PHARMACY.nearbyPharmacySearchForm.unableToGetCurrentLocation"),
            type: "warning",
            style: "toast",
          });
          searchNearbyPharmacyForm.setValue("useCurrentLocation", false);
        },
      );
    }
  }, [useCurrentLocation]);

  useEffect(() => {
    searchNearbyPharmacyForm.reset({ ...defaultValues });
  }, [searchNear]);

  return (
    <Form form={searchNearbyPharmacyForm} onSubmit={onSearchSubmitted}>
      <Grid spacing="mini">
        <Grid.Spread columns={12}>
          <Stack spacing="mini">
            <Textbox.Field
              testId="location"
              style={{ flexGrow: 1 }}
              disabled={useCurrentLocation}
              name="location"
              required
              placeholder={translate("NEARBY_PHARMACY.nearbyPharmacySearchForm.searchCityOrZipCode")}
              label={translate("NEARBY_PHARMACY.nearbyPharmacySearchForm.searchCityOrZipCode")}
            />
            <Shelf>
              <ThemedCheckbox.Field
                hideLabel
                label={translate("NEARBY_PHARMACY.nearbyPharmacySearchForm.useMyCurrentLocation")}
                name="useCurrentLocation"
                testId="location-entry-type-selection"
              />
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        </Grid.Spread>
        <Grid.Span columns={12}>
          <Shelf alignment="center">
            <ThemedSubmitButton
              disabled={useCurrentLocation && (currentLocation.latitude === undefined || currentLocation.longitude === undefined)}
              iconPosition="right"
              icon="arrow-right"
              testId="search-pharmacies"
            >
              {translate("NEARBY_PHARMACY.nearbyPharmacySearchForm.searchPharmacies")}
            </ThemedSubmitButton>
          </Shelf>
        </Grid.Span>
      </Grid>
    </Form>
  );
};
